/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @file EnrollmentInformation.jsx
 * @author Anuja Gaikwad
 * @since 1.4.9
 */

import React from 'react';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CustomCard from './CustomCard';
import { BadgeTemplate } from '../tabular/Tabular';
import mixins from '../../assets/styles/mixins.scss';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import ConfirmationCard from './ConfirmationCard';

const StyledCustomCard = styled(CustomCard)({
  '&&': {
    display: 'flex',
    minHeight: '200px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    alignSelf: 'stretch',
    borderRadius: '16px'
  }
});

const ScrollContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    '& .MuiChip-labelSmall, & .MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0
    }
  }
});

const Title = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  }
});

const ContentWrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%'
  }
});

const Row = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  }
});

const Label = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight22,
    fontFamily: mixins.fontFamilyPlusSans
  }
});

const Value = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: mixins.lineHeight125
  }
});

const ChipBadge = styled('div')({
  '&&': {
    '& .MuiChip-root': {
      padding: '2px 6px',
      boxSizing: 'border-box'
    }
  }
});

function EnrollmentInformation({
  item,
  isConfirmationCardOpen,
  setIsConfirmationCardOpen,
  textInfo,
  handleConfirmDelete,
  classes
}) {
  const hasValue = (value) => value?.toString().trim();
  const normalizeKey = (val) => String(val || '').toLowerCase();

  const RowComponent = classes?.row ? 'div' : Row;
  const LabelComponent = classes?.label ? Typography : Label;
  const ValueComponent = classes?.value ? Typography : Value;
  const TitleComponent = classes?.title ? Typography : Title;
  const CardComponent = classes?.card ? CustomCard : StyledCustomCard;
  const ScrollContainerComponent = classes?.scrollContainer ? 'div' : ScrollContainer;
  const ContentWrapperComponent = classes?.contentWrapper ? 'div' : ContentWrapper;

  const renderRow = ({ label, text, value, isBadge = false }) => {
    if (!hasValue(value)) {
      return null;
    }

    const renderRowValue = () => {
      if (!isBadge) {
        return (
          <ValueComponent className={classes?.value}>
            {value}
          </ValueComponent>
        );
      }

      const badge = (
        <BadgeTemplate
          label={text || value}
          status={normalizeKey(value)}
          showIcon={false}
          classes={classes?.chip ? { badge: classes.chip } : undefined}
        />
      );

      if (classes?.chip) {
        return badge;
      }

      return <ChipBadge>{badge}</ChipBadge>;
    };

    return (
      <RowComponent className={classes?.row}>
        <LabelComponent className={classes?.label}>
          {label}
        </LabelComponent>
        {renderRowValue()}
      </RowComponent>
    );
  };

  const confirmationClasses = {};
  [
    'paper',
    'titleText',
    'contentText',
    'cancelButtonText',
    'confirmButtonText',
    'confirmButton',
    'cancelButton',
    'actions'
  ].forEach((key) => {
    if (classes?.[key]) {
      confirmationClasses[key] = classes[key];
    }
  });

  return (
    <CardComponent className={classes?.card}>
      <CustomScrollbar autoHeight>
        <ScrollContainerComponent className={classes?.scrollContainer}>
          <TitleComponent className={classes?.title}>
            {textInfo.enrolmentCardTitle}
          </TitleComponent>

          <ContentWrapperComponent className={classes?.contentWrapper}>
            {renderRow({
              label: textInfo.statusLabel,
              text: item.status,
              value: item.statusValue,
              isBadge: true
            })}

            {renderRow({
              label: textInfo.enrolledLabel,
              value: item.enrolledDate
            })}

            {renderRow({
              label: textInfo.accessLabel,
              value: item.access
            })}

            {renderRow({
              label: textInfo.roleLabel,
              text: item.role,
              value: item.roleValue,
              isBadge: true
            })}
          </ContentWrapperComponent>
        </ScrollContainerComponent>
      </CustomScrollbar>

      <ConfirmationCard
        open={isConfirmationCardOpen}
        onClose={() => setIsConfirmationCardOpen(false)}
        title={textInfo?.title}
        content={textInfo?.content}
        cancelText={textInfo?.cancelText}
        confirmText={textInfo?.deleteConfirmationConfirmText}
        onCancel={() => setIsConfirmationCardOpen(false)}
        onConfirm={handleConfirmDelete}
        showCloseButton
        classes={confirmationClasses}
      />
    </CardComponent>
  );
}

EnrollmentInformation.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    statusValue: PropTypes.string,
    enrolledDate: PropTypes.string,
    access: PropTypes.string,
    role: PropTypes.string,
    roleValue: PropTypes.string
  }),
  isConfirmationCardOpen: PropTypes.bool,
  setIsConfirmationCardOpen: PropTypes.func,
  textInfo: PropTypes.shape({
    deleteConfirmationConfirmText: PropTypes.string,
    enrolmentCardTitle: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    cancelText: PropTypes.string,
    statusLabel: PropTypes.string,
    enrolledLabel: PropTypes.string,
    accessLabel: PropTypes.string,
    roleLabel: PropTypes.string
  }),
  handleConfirmDelete: PropTypes.func,
  classes: PropTypes.object
};

EnrollmentInformation.defaultProps = {
  item: {
    id: '',
    status: '',
    enrolledDate: '',
    access: '',
    role: ''
  },
  isConfirmationCardOpen: false,
  setIsConfirmationCardOpen: () => {},
  textInfo: {
    deleteConfirmationConfirmText: '',
    enrolmentCardTitle: '',
    title: '',
    content: '',
    cancelText: '',
    statusLabel: '',
    enrolledLabel: '',
    accessLabel: '',
    roleLabel: ''
  },
  handleConfirmDelete: () => {},
  classes: {}
};

const EnrollmentInformationWithStyles = (props) => {
  const { classes: parentClasses = {}, ...rest } = props;

  return <EnrollmentInformation {...rest} classes={parentClasses} />;
};

EnrollmentInformationWithStyles.propTypes = {
  classes: PropTypes.object
};

EnrollmentInformationWithStyles.defaultProps = {
  classes: {}
};

export default EnrollmentInformationWithStyles;
